import { createContext, ReactNode, useContext } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { OdinIcon } from '@/components/ui/odin/odin-icon'

type ModalContextValue = {
  onClose: () => void
}

const ModalContext = createContext<ModalContextValue | null>(null)

const useModalContext = () => {
  const ctx = useContext(ModalContext)
  if (!ctx) throw new Error('Modal.* subcomponents must be used inside <Modal>')
  return ctx
}

const ICON_BUTTON_CLASSES =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-0 bg-transparent cursor-pointer text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'

export type ModalProps = {
  /** Dismiss handler shared with the header's close control. */
  onClose: () => void
  children: ReactNode
  /** Extra classes for the dialog root. */
  className?: string
}

/**
 * Popup content shell. role/aria-modal live on the outlet's Dialog.Popup;
 * aria-labelledby is wired via Dialog.Title in ModalHeader. Owns padding and
 * the shared close handler. Compose with ModalHeader / ModalBody / ModalFooter.
 */
export const Modal = ({ onClose, children, className }: ModalProps) => (
  <ModalContext.Provider value={{ onClose }}>
    <section className={clsx('flex flex-col p-5', className)}>{children}</section>
  </ModalContext.Provider>
)

export type ModalHeaderProps = {
  title: string
  /** Brand-tinted glyph shown before the title. */
  icon?: IconName
  /** Renders a back affordance before the icon/title. */
  onBack?: () => void
}

export const ModalHeader = ({ title, icon, onBack }: ModalHeaderProps) => {
  const { t } = useTranslation()
  const { onClose } = useModalContext()

  return (
    <header className="mb-5 flex items-center gap-3">
      {onBack && (
        <button type="button" aria-label={t('goBack')} onClick={onBack} className={ICON_BUTTON_CLASSES}>
          <OdinIcon aria-hidden icon={['far', 'arrow-left']} />
        </button>
      )}

      {icon && (
        <span
          aria-hidden
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
        >
          <OdinIcon icon={['far', icon]} className="h-[18px] w-[18px]" />
        </span>
      )}

      <DialogPrimitive.Title
        render={<h2 />}
        className="line-clamp-2 min-w-0 flex-1 text-lg leading-tight font-bold text-foreground"
      >
        {title}
      </DialogPrimitive.Title>

      <button type="button" aria-label={t('close')} onClick={onClose} className={ICON_BUTTON_CLASSES}>
        <OdinIcon aria-hidden icon={['far', 'times']} />
      </button>
    </header>
  )
}

export type ModalBodyProps = {
  children: ReactNode
  className?: string
}

export const ModalBody = ({ children, className }: ModalBodyProps) => (
  <div className={clsx('flex flex-col', className)}>{children}</div>
)

export type ModalFooterProps = {
  children: ReactNode
  className?: string
}

export const ModalFooter = ({ children, className }: ModalFooterProps) => (
  <div className={clsx('mt-6 w-full', className)}>{children}</div>
)
